import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Shield, Plus, Trash2, Edit2, Check, ChevronDown,
    Loader2, AlertCircle, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Dialog, Button, RolesSkeleton, PermissionsSkeleton } from '../../shared/custom-ui';
import ApiServices from '../../shared/ApiServices/ApiServices';

interface Role {
    name: string;
    permissions: string[];
}

const formatPermission = (perm: string): string => {
    return perm
        .split(':')
        .map(part =>
            part
                .replace(/[-_]/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase())
        )
        .join(': ');
};

/** Human-readable one-liner for a role (used on the card) */
const getRoleDescription = (name: string, permCount: number): string => {
    const n = name.toLowerCase();
    if (n.includes('admin')) return 'Full platform access with administrative privileges.';
    if (n.includes('doctor') || n.includes('physician')) return 'Clinical access for patient care and documentation.';
    if (n.includes('patholog')) return 'Pathology lab — from specimen registration to report delivery.';
    if (n.includes('lab') || n.includes('techni')) return 'Laboratory workflow: specimen collection through results.';
    if (n.includes('nurse')) return 'Patient care support with clinical view access.';
    if (n.includes('recept')) return 'Front-desk operations and scheduling management.';
    if (n.includes('radiol')) return 'Radiology imaging, reporting and study management.';
    if (permCount === 0) return 'No permissions assigned to this role yet.';
    if (permCount > 80) return `Broad access — ${permCount} system actions enabled.`;
    return `Access to ${permCount} specific system action${permCount !== 1 ? 's' : ''}.`;
};

/** Return unique module names (the resource prefix before ':') sorted A-Z */
const getAccessGroups = (permissions: string[]): string[] => {
    const groups = new Set<string>();
    permissions.forEach(p => {
        const resource = p.split(':')[0] ?? p;
        groups.add(resource.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    });
    return Array.from(groups).sort();
};

// ─── Permission checkbox group ─────────────────────────────────────────────────
const PermissionCheckboxes: React.FC<{
    allPermissions: string[];
    selected: string[];
    onChange: (perms: string[]) => void;
    disabled?: boolean;
}> = ({ allPermissions, selected, onChange, disabled }) => {
    const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(new Set());

    // Group permissions by resource prefix
    const groups = React.useMemo(() => {
        const acc: Record<string, string[]> = {};
        allPermissions.forEach(perm => {
            const resource = perm.split(':')[0] ?? perm;
            const key = resource.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            if (!acc[key]) acc[key] = [];
            acc[key].push(perm);
        });
        return acc;
    }, [allPermissions]);

    const groupKeys = Object.keys(groups).sort();

    const toggleAll = () => onChange(selected.length === allPermissions.length ? [] : [...allPermissions]);

    const toggleGroup = (group: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        const gp = groups[group];
        const allSel = gp.every(p => selected.includes(p));
        onChange(allSel ? selected.filter(p => !gp.includes(p)) : Array.from(new Set([...selected, ...gp])));
    };

    const toggleCollapse = (group: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            next.has(group) ? next.delete(group) : next.add(group);
            return next;
        });
    };

    if (!allPermissions.length) {
        return <p className="text-[13px] text-[var(--v2-text-muted)] italic py-2">No permissions available.</p>;
    }

    return (
        <div className="flex flex-col gap-3">
            {/* Summary + Select All */}
            <div className="flex items-center justify-between py-1">
                <span className="text-[13px] font-medium" style={{ color: 'var(--v2-text-secondary)' }}>
                    <span className="font-bold" style={{ color: 'var(--v2-primary-600, #2563eb)' }}>{selected.length}</span>
                    {' '}of {allPermissions.length} permissions selected
                </span>
                <button
                    type="button"
                    onClick={toggleAll}
                    disabled={disabled}
                    style={{ outline: 'none' }}
                    className="text-[12px] font-semibold text-[var(--v2-primary-600,#045eb8)] hover:opacity-80 transition-opacity"
                >
                    {selected.length === allPermissions.length ? 'Deselect All' : 'Select All'}
                </button>
            </div>

            <div className="flex flex-col gap-2 min-h-0 pr-1">
                {groupKeys.map(group => {
                    const gp = groups[group];
                    const selCount = gp.filter(p => selected.includes(p)).length;
                    const allSel = selCount === gp.length;
                    const someSel = selCount > 0 && !allSel;
                    const collapsed = collapsedGroups.has(group);

                    return (
                        <div
                            key={group}
                            className="rounded-xl border overflow-hidden"
                            style={{ borderColor: allSel ? 'var(--v2-primary-300, #93c5fd)' : 'var(--v2-border-card)' }}
                        >
                            {/* Group header */}
                            <div
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                                style={{ background: allSel ? 'var(--v2-primary-50, #eff6ff)' : 'var(--v2-bg-surface)' }}
                                onClick={() => toggleCollapse(group)}
                            >
                                {/* Group checkbox */}
                                <button
                                    type="button"
                                    onClick={(e) => toggleGroup(group, e)}
                                    disabled={disabled}
                                    className="flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all"
                                    style={{
                                        outline: 'none',
                                        borderColor: allSel
                                            ? 'var(--v2-primary-500, #3b82f6)'
                                            : someSel
                                                ? 'var(--v2-primary-400, #60a5fa)'
                                                : 'var(--v2-border-interactive)',
                                        background: allSel
                                            ? 'var(--v2-primary-500, #3b82f6)'
                                            : 'transparent',
                                    }}
                                >
                                    {allSel && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                    {someSel && !allSel && (
                                        <span className="w-2 h-0.5 rounded" style={{ background: 'var(--v2-primary-600, #2563eb)' }} />
                                    )}
                                </button>

                                <div className="flex-1 flex items-center gap-2">
                                    <span
                                        className="text-[13px] font-bold"
                                        style={{ color: allSel ? 'var(--v2-primary-700, #1d4ed8)' : 'var(--v2-text-heading)' }}
                                    >
                                        {group}
                                    </span>
                                    <span
                                        className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                                        style={{
                                            background: allSel ? 'var(--v2-primary-100, #dbeafe)' : 'rgba(0,0,0,0.05)',
                                            color: allSel ? 'var(--v2-primary-700, #1d4ed8)' : 'var(--v2-text-muted)',
                                        }}
                                    >
                                        {selCount}/{gp.length}
                                    </span>
                                </div>

                                <ChevronDown
                                    className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
                                    style={{
                                        color: 'var(--v2-text-muted)',
                                        transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                    }}
                                />
                            </div>

                            {/* Individual permissions */}
                            {!collapsed && (
                                <div
                                    className="grid grid-cols-2 gap-1.5 p-3"
                                    style={{
                                        borderTop: '1px solid var(--v2-border-subtle)',
                                        background: 'var(--v2-bg-card)',
                                    }}
                                >
                                    {gp.map(perm => {
                                        const checked = selected.includes(perm);
                                        const action = (perm.split(':')[1] ?? perm).replace(/[-_]/g, ' ');
                                        return (
                                            <button
                                                key={perm}
                                                type="button"
                                                onClick={() => !disabled && onChange(
                                                    checked ? selected.filter(p => p !== perm) : [...selected, perm]
                                                )}
                                                disabled={disabled}
                                                style={{ outline: 'none' }}
                                                className={[
                                                    'flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-all text-[12px] font-medium',
                                                    checked
                                                        ? 'border-[var(--v2-primary-300,#93c5fd)] bg-[var(--v2-primary-50,#eff6ff)] text-[var(--v2-primary-700,#1d4ed8)]'
                                                        : 'border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] text-[var(--v2-text-secondary)] hover:border-[var(--v2-border-interactive)]',
                                                    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                                                ].join(' ')}
                                            >
                                                <span
                                                    className="w-3.5 h-3.5 rounded-sm flex-shrink-0 border flex items-center justify-center transition-all"
                                                    style={{
                                                        background: checked ? 'var(--v2-primary-500, #3b82f6)' : 'transparent',
                                                        borderColor: checked ? 'var(--v2-primary-500, #3b82f6)' : 'var(--v2-border-interactive)',
                                                    }}
                                                >
                                                    {checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                                </span>
                                                <span className="truncate capitalize">{action}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


// ─── Role Card ────────────────────────────────────────────────────────────────
const ROLE_COLORS: Record<string, string> = {
    admin: '#7c3aed', administrator: '#7c3aed', administrators: '#7c3aed',
    doctor: '#0369a1', physician: '#0369a1',
    pathologist: '#0f766e', pathology: '#0f766e',
    lab_technician: '#b45309', technician: '#b45309',
    nurse: '#be185d',
    receptionist: '#4f46e5',
    radiologist: '#1d6fb8',
};
const getRoleColor = (name: string) => ROLE_COLORS[name.toLowerCase()] ?? '#374151';

const RoleCard: React.FC<{
    role: Role;
    onEdit: (role: Role) => void;
    onDelete: (roleName: string) => void;
    onViewAll: (role: Role) => void;
    deleting: boolean;
    editLoading: boolean;
}> = ({ role, onEdit, onDelete, onViewAll, deleting, editLoading }) => {
    const color = getRoleColor(role.name);
    const description = getRoleDescription(role.name, role.permissions.length);
    const accessGroups = getAccessGroups(role.permissions);
    const MAX_GROUPS = 4;
    const visibleGroups = accessGroups.slice(0, MAX_GROUPS);
    const remainingGroups = accessGroups.length - MAX_GROUPS;
    // Progress bar width (cap at 100%)
    const progressPct = Math.min(100, Math.round((role.permissions.length / 150) * 100));

    return (
        <div
            className="group relative flex flex-col rounded-2xl border bg-[var(--v2-bg-card)] shadow-[var(--v2-shadow-card)] hover:shadow-[var(--v2-shadow-card-hover)] transition-all duration-200 overflow-hidden"
            style={{ borderColor: 'var(--v2-border-card)' }}
        >
            {/* Gradient top accent bar */}
            <div
                className="h-1.5 w-full flex-shrink-0"
                style={{ background: `linear-gradient(90deg, ${color}, ${color}60)` }}
            />

            <div className="flex flex-col gap-4 p-5 flex-1">

                {/* Header: shield icon + role name + actions */}
                <div className="flex items-start gap-3">
                    <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${color}18` }}
                    >
                        <Shield className="w-5 h-5" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-[15px] text-[var(--v2-text-heading)] truncate capitalize">
                            {role.name}
                        </h4>
                        <p className="text-[11px] font-medium text-[var(--v2-text-muted)] mt-0.5 leading-snug">
                            {description}
                        </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={() => onEdit(role)}
                            disabled={editLoading}
                            className="p-2 rounded-lg text-[var(--v2-text-muted)] hover:text-[var(--v2-primary-600,#2563eb)] hover:bg-[var(--v2-bg-hover)] transition-colors disabled:opacity-50"
                            style={{ outline: 'none' }}
                            title="Edit permissions"
                        >
                            {editLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit2 className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={() => onDelete(role.name)}
                            disabled={deleting}
                            className="p-2 rounded-lg text-[var(--v2-text-muted)] hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                            style={{ outline: 'none' }}
                            title="Delete role"
                        >
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                {/* Permission count + progress bar */}
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                        <span
                            className="text-[10px] font-black uppercase tracking-wider"
                            style={{ color: 'var(--v2-text-muted)' }}
                        >
                            Permissions
                        </span>
                        <span className="text-[12px] font-bold" style={{ color }}>
                            {role.permissions.length}
                        </span>
                    </div>
                    <div
                        className="h-1.5 w-full rounded-full overflow-hidden"
                        style={{ background: `${color}18` }}
                    >
                        <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ background: color, width: `${progressPct}%` }}
                        />
                    </div>
                </div>

                {/* Access groups (module-level chips — much more readable than raw permissions) */}
                {accessGroups.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                        <p
                            className="text-[10px] font-black uppercase tracking-wider"
                            style={{ color: 'var(--v2-text-muted)' }}
                        >
                            Access to
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {visibleGroups.map(group => (
                                <span
                                    key={group}
                                    className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold"
                                    style={{ background: `${color}12`, color, border: `1px solid ${color}25` }}
                                >
                                    {group}
                                </span>
                            ))}
                            {remainingGroups > 0 && (
                                <button
                                    type="button"
                                    onClick={() => onViewAll(role)}
                                    className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all hover:opacity-80 active:scale-95"
                                    style={{
                                        outline: 'none',
                                        background: `${color}18`,
                                        color,
                                        border: `1px solid ${color}30`,
                                        cursor: 'pointer',
                                    }}
                                    title={`View all ${role.permissions.length} permissions`}
                                >
                                    +{remainingGroups} more
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
                        style={{
                            background: 'var(--v2-bg-surface)',
                            border: '1px solid var(--v2-border-subtle)',
                        }}
                    >
                        <AlertCircle className="w-4 h-4 flex-shrink-0 text-[var(--v2-text-muted)]" />
                        <span className="text-[12px] text-[var(--v2-text-muted)]">No permissions assigned yet</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── View All Permissions Modal ───────────────────────────────────────────────
const PermissionsViewModal: React.FC<{
    role: Role | null;
    onClose: () => void;
}> = ({ role, onClose }) => {
    const [search, setSearch] = useState('');
    const color = role ? getRoleColor(role.name) : '#374151';

    // Reset search when modal opens/closes
    useEffect(() => { if (!role) setSearch(''); }, [role]);

    const filtered = role
        ? role.permissions.filter(p =>
            formatPermission(p).toLowerCase().includes(search.toLowerCase()) ||
            p.toLowerCase().includes(search.toLowerCase())
        )
        : [];

    // Group by resource (the part before ':')
    const grouped = filtered.reduce<Record<string, string[]>>((acc, p) => {
        const resource = p.split(':')[0] ?? p;
        const key = resource.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        if (!acc[key]) acc[key] = [];
        acc[key].push(p);
        return acc;
    }, {});
    const groupKeys = Object.keys(grouped).sort();

    return (
        <Dialog
            isOpen={Boolean(role)}
            onClose={onClose}
            title={`${role?.name ?? ''} — All Permissions`}
            description={`${role?.permissions.length ?? 0} permissions assigned to this role`}
            size="medium"
        >
            {/* Search */}
            <div className="relative mb-4">
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search permissions…"
                    className="w-full rounded-lg border px-3 py-2 pl-9 text-sm text-[var(--v2-text-primary)] bg-[var(--v2-bg-surface)] outline-none transition-colors focus:border-[var(--v2-primary-400,#3b82f6)]"
                    style={{ borderColor: 'var(--v2-border-interactive)' }}
                />
                <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                    style={{ color: 'var(--v2-text-muted)' }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
            </div>

            {/* Grouped permissions */}
            <div className="flex flex-col gap-4 max-h-[420px] overflow-y-auto pr-1">
                {groupKeys.length === 0 ? (
                    <p className="text-center text-[13px] text-[var(--v2-text-muted)] py-8">
                        No permissions match "{search}"
                    </p>
                ) : (
                    groupKeys.map(group => (
                        <div key={group}>
                            {/* Group header */}
                            <div className="flex items-center gap-2 mb-2">
                                <span
                                    className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                                    style={{ background: `${color}15`, color }}
                                >
                                    {group}
                                </span>
                                <div className="flex-1 h-px" style={{ background: 'var(--v2-border-subtle)' }} />
                            </div>
                            {/* Permission badges in group */}
                            <div className="flex flex-wrap gap-1.5">
                                {grouped[group].map(p => (
                                    <span
                                        key={p}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium"
                                        style={{ background: `${color}10`, color, border: `1px solid ${color}25` }}
                                    >
                                        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                                        {formatPermission(p)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 mt-2 border-t border-[var(--v2-border-subtle)]">
                <span className="text-[12px] text-[var(--v2-text-muted)]">
                    {search ? `${filtered.length} of ${role?.permissions.length} shown` : `${role?.permissions.length} total`}
                </span>
                <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
        </Dialog>
    );
};

// ─── Role Form Modal ──────────────────────────────────────────────────────────
const RoleFormModal: React.FC<{
    isOpen: boolean;
    editingRole: Role | null;
    allPermissions: string[];
    permsLoading: boolean;
    editPermsLoading: boolean;
    onClose: () => void;
    onSave: (name: string, permissions: string[]) => Promise<void>;
    saving: boolean;
}> = ({ isOpen, editingRole, allPermissions, permsLoading, editPermsLoading, onClose, onSave, saving }) => {
    const [name, setName] = useState('');
    const [selectedPerms, setSelectedPerms] = useState<string[]>([]);

    useEffect(() => {
        if (isOpen) {
            setName(editingRole?.name ?? '');
            setSelectedPerms(editingRole?.permissions ?? []);
        }
    }, [isOpen, editingRole]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) { toast.error('Role name is required'); return; }
        if (!selectedPerms.length) { toast.error('Select at least one permission'); return; }
        await onSave(name.trim(), selectedPerms);
    };

    const isEdit = Boolean(editingRole);
    const showSkeleton = allPermissions.length === 0 && (permsLoading || editPermsLoading);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? `Edit "${editingRole?.name}" Permissions` : 'Create New Role'}
            description={isEdit ? 'Update the permissions assigned to this role.' : 'Define the role name and select the permissions it grants.'}
            size="medium"
            preventCloseOnOverlayClick
            footer={
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button variant="primary" type="submit" form="role-form" loading={saving} disabled={saving || showSkeleton}>
                        {isEdit ? 'Save Changes' : 'Create Role'}
                    </Button>
                </div>
            }
        >
            <form id="role-form" onSubmit={handleSubmit} className="flex flex-col gap-4 h-full">
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <label className="text-[12px] font-semibold text-[var(--v2-text-secondary)]">
                        Role Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        disabled={isEdit || saving}
                        placeholder="e.g. Radiologist, Nurse, Admin"
                        className={[
                            'w-full rounded-lg border px-3 py-2.5 text-sm text-[var(--v2-text-primary)] bg-[var(--v2-bg-surface)] outline-none transition-colors',
                            'focus:border-[var(--v2-primary-400,#3b82f6)] focus:ring-2 focus:ring-[var(--v2-primary-200,#bfdbfe)]',
                            isEdit ? 'opacity-60 cursor-not-allowed bg-[var(--v2-bg-card)]' : '',
                        ].join(' ')}
                        style={{ borderColor: 'var(--v2-border-interactive)' }}
                    />
                    {isEdit && (
                        <p className="text-[11px] text-[var(--v2-text-muted)]">Role name cannot be changed after creation.</p>
                    )}
                </div>

                <div className="flex flex-col gap-1.5 flex-1 min-h-0">
                    <label className="text-[12px] font-semibold text-[var(--v2-text-secondary)] flex-shrink-0">
                        Permissions <span className="text-red-500">*</span>
                    </label>
                    {showSkeleton
                        ? <PermissionsSkeleton />
                        : (
                            <PermissionCheckboxes
                                allPermissions={allPermissions}
                                selected={selectedPerms}
                                onChange={setSelectedPerms}
                                disabled={saving}
                            />
                        )}
                </div>
            </form>
        </Dialog>
    );
};

// ─── Delete Confirm Modal ──────────────────────────────────────────────────────
const DeleteRoleConfirm: React.FC<{
    roleName: string | null;
    onConfirm: () => void;
    onCancel: () => void;
    deleting: boolean;
}> = ({ roleName, onConfirm, onCancel, deleting }) => (
    <Dialog isOpen={Boolean(roleName)} onClose={onCancel} size="small" preventCloseOnOverlayClick showCloseButton={false}>
        <div className="-mt-2 flex flex-col gap-4">
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                    <AlertCircle className="h-5 w-5 text-red-600" strokeWidth={2.5} />
                </div>
                <h2 className="m-0 text-[15px] font-bold text-[var(--v2-text-heading)]">Delete "{roleName}"?</h2>
            </div>
            <p className="m-0 text-[13px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
                This will permanently remove the role. Members assigned this role will lose its permissions.
            </p>
            <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={onCancel} disabled={deleting}>Keep Role</Button>
                <Button variant="danger" onClick={onConfirm} loading={deleting}>Delete Role</Button>
            </div>
        </div>
    </Dialog>
);


// ─── Main RolesTab Component ───────────────────────────────────────────────────
const RolesTab: React.FC<{
    search?: string;
    createTriggerRef?: React.MutableRefObject<(() => void) | null>;
    onRolesChanged?: () => void;
}> = ({ search = '', createTriggerRef, onRolesChanged }) => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [allPermissions, setAllPermissions] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [permsLoading, setPermsLoading] = useState(false);
    const [editPermsLoading, setEditPermsLoading] = useState(false);
    const [editingRoleName, setEditingRoleName] = useState<string | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
    const [deletingRole, setDeletingRole] = useState<string | null>(null);
    const [viewingRole, setViewingRole] = useState<Role | null>(null);

    const allPermissionsRef = useRef<string[]>([]);

    // ── Client-side search filter ─────────────────────────────────────────────
    const filteredRoles = search.trim()
        ? roles.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
        : roles;

    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);


    const normaliseRoles = (res: any): Role[] => {
        const list: any[] = Array.isArray(res) ? res : (res?.roles ?? res?.data ?? []);
        return list.map(r => ({
            name: r.name ?? r.roleName ?? r.role ?? String(r),
            permissions: Array.isArray(r.permissions) ? r.permissions : [],
        }));
    };

    const normalisePermissions = (res: any): string[] => {
        const list: any[] = Array.isArray(res) ? res : (res?.permissions ?? res?.data ?? []);
        return list.map((p: any) => (typeof p === 'string' ? p : p?.name ?? p?.permission ?? String(p)));
    };

    const loadRoles = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const res = await ApiServices.getAllRoles();
            if (mountedRef.current) setRoles(normaliseRoles(res));
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load roles';
            if (mountedRef.current) setFetchError(msg);
            toast.error(msg);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, []);

    const loadPermissions = useCallback(async () => {
        if (allPermissionsRef.current.length > 0) return;
        setPermsLoading(true);
        try {
            const res = await ApiServices.getAllPermissions();
            if (mountedRef.current) {
                const perms = normalisePermissions(res);
                allPermissionsRef.current = perms;
                setAllPermissions(perms);
            }
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to load permissions');
        } finally {
            setPermsLoading(false);
        }
    }, []);

    useEffect(() => { loadRoles(); }, [loadRoles]);

    const openCreate = useCallback(async () => {
        setEditingRole(null);
        setFormOpen(true);
        await loadPermissions();
    }, [loadPermissions]);

    // Register openCreate on the parent's ref so TeamManagement header button can trigger it
    useEffect(() => {
        if (createTriggerRef) createTriggerRef.current = openCreate;
        return () => { if (createTriggerRef) createTriggerRef.current = null; };
    }, [createTriggerRef, openCreate]);

    const openEdit = async (role: Role) => {
        // 1. Pre-fill with cached data so modal shows title immediately
        setEditingRole({ name: role.name, permissions: role.permissions });
        setEditingRoleName(role.name);
        // 2. Open the modal RIGHT AWAY — no waiting for API
        setFormOpen(true);
        setEditPermsLoading(true);

        try {
            // 3. Fetch fresh permissions + catalogue concurrently in the background
            await Promise.all([
                loadPermissions(),
                (async () => {
                    try {
                        const res = await ApiServices.getRolePermissions(role.name);
                        const freshPerms: string[] = Array.isArray(res?.permissions)
                            ? res.permissions
                            : Array.isArray(res) ? res : role.permissions;
                        if (mountedRef.current) {
                            setEditingRole({ name: role.name, permissions: freshPerms });
                        }
                    } catch {
                    }
                })(),
            ]);
        } finally {
            if (mountedRef.current) {
                setEditPermsLoading(false);
                setEditingRoleName(null);
            }
        }
    };

    const handleSave = async (name: string, permissions: string[]) => {
        setSaving(true);
        try {
            if (editingRole) {
                await ApiServices.updateRolePermissions(name, permissions);
                toast.success(`"${name}" permissions updated`);
            } else {
                await ApiServices.createRole({ name, permissions });
                toast.success(`Role "${name}" created`);
            }
            setFormOpen(false);
            loadRoles();
            onRolesChanged?.();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Operation failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeletingRole(target);
        setDeleteTarget(null);
        try {
            await ApiServices.deleteRole(target);
            toast.success(`Role "${target}" deleted`);
            if (mountedRef.current) setRoles(prev => prev.filter(r => r.name !== target));
            onRolesChanged?.();
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Delete failed');
        } finally {
            if (mountedRef.current) setDeletingRole(null);
        }
    };

    return (
        <div className="flex flex-col gap-5">

            {loading ? (
                <RolesSkeleton />
            ) : fetchError ? (
                // ── Error state ───────────────────────────────────────────────────
                <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                    <div
                        className="w-20 h-20 mb-6 rounded-2xl flex items-center justify-center"
                        style={{ background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.18)' }}
                    >
                        <AlertCircle className="w-9 h-9" style={{ color: '#ef4444' }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[18px] font-bold mb-2" style={{ color: 'var(--v2-text-heading)' }}>
                        Failed to load roles
                    </h3>
                    <p className="text-[14px] max-w-sm mx-auto mb-6" style={{ color: 'var(--v2-text-muted)' }}>
                        {fetchError}
                    </p>
                    <button
                        onClick={loadRoles}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                        style={{ background: '#ef4444', outline: 'none' }}
                    >
                        <RefreshCw className="w-4 h-4" />
                        Try Again
                    </button>
                </div>
            ) : filteredRoles.length === 0 && search.trim() ? (
                // ── No search results ──
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <Shield className="w-12 h-12 mb-3" style={{ color: 'var(--v2-text-muted)', opacity: 0.3 }} strokeWidth={1.4} />
                    <p className="text-[15px] font-bold mb-1" style={{ color: 'var(--v2-text-heading)' }}>No roles match "{search}"</p>
                    <p className="text-[13px]" style={{ color: 'var(--v2-text-muted)' }}>Try a different role name.</p>
                </div>
            ) : roles.length === 0 ? (
                // ── Empty state ──────────────────────────────────────────────────
                <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                    {/* Layered icon */}
                    <div className="relative mb-7">
                        <div
                            className="w-24 h-24 rounded-3xl flex items-center justify-center"
                            style={{
                                background: 'color-mix(in srgb, var(--v2-primary-500, #3b82f6) 8%, transparent)',
                                border: '1.5px solid color-mix(in srgb, var(--v2-primary-500, #3b82f6) 20%, transparent)',
                            }}
                        >
                            <Shield
                                className="w-11 h-11"
                                style={{ color: 'var(--v2-primary-500, #3b82f6)' }}
                                strokeWidth={1.4}
                            />
                        </div>
                        {/* Floating + badge */}
                        <div
                            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
                            style={{ background: 'var(--v2-primary-500, #3b82f6)', boxShadow: '0 2px 8px rgba(59,130,246,0.4)' }}
                        >
                            <Plus className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                        </div>
                    </div>

                    <h3 className="text-[20px] font-bold mb-2" style={{ color: 'var(--v2-text-heading)' }}>
                        No roles yet
                    </h3>
                    <p className="text-[14px] max-w-sm mx-auto mb-7 leading-relaxed" style={{ color: 'var(--v2-text-muted)' }}>
                        Roles control what your team can access. Create your first role and assign granular permissions to keep your platform secure.
                    </p>
                    <button
                        onClick={openCreate}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                        style={{ background: 'var(--v2-primary-600, #2563eb)', outline: 'none', boxShadow: '0 4px 14px rgba(37,99,235,0.35)' }}
                    >
                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                        Create Your First Role
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredRoles.map(role => (
                        <RoleCard
                            key={role.name}
                            role={role}
                            onEdit={openEdit}
                            onDelete={name => setDeleteTarget(name)}
                            onViewAll={setViewingRole}
                            deleting={deletingRole === role.name}
                            editLoading={editingRoleName === role.name}
                        />
                    ))}
                </div>
            )}

            <RoleFormModal
                isOpen={formOpen}
                editingRole={editingRole}
                allPermissions={allPermissions}
                permsLoading={permsLoading}
                editPermsLoading={editPermsLoading}
                onClose={() => {
                    setFormOpen(false);
                    setEditPermsLoading(false);
                    setEditingRoleName(null);
                }}
                onSave={handleSave}
                saving={saving}
            />
            <DeleteRoleConfirm
                roleName={deleteTarget}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
                deleting={Boolean(deletingRole)}
            />
            <PermissionsViewModal
                role={viewingRole}
                onClose={() => setViewingRole(null)}
            />
        </div>
    );
};

export default RolesTab;
